<script setup lang="ts">
import { computed } from 'vue'

/**
 * The only way to render an `ok`/`danger` message: the icon and the tinted surface are
 * built in, not passed by the caller, so a colour-only treatment is not constructible.
 * See AGENTS.md's colour vocabulary, rule 1. `neutral` is a third, informational tone -
 * for a state that is neither a success nor a failure (e.g. "no data yet"). `wait` is a
 * fourth: not settled yet but still on its way (a target in progress with its goal date
 * ahead), in the attention colour with an hourglass.
 *
 * `verdict` swaps ok's check and danger's warning for a thumb up and a thumb down - for a
 * judgement on a figure (a target met or missed), not the outcome of an operation.
 * `size="sm"` is the pill a card uses beside a figure.
 */
const props = defineProps<{
  tone: 'ok' | 'danger' | 'neutral' | 'wait'
  verdict?: boolean
  size?: 'sm' | 'md'
}>()

const TONE = {
  ok: {
    text: 'text-[color:var(--color-ok)]',
    surface: 'bg-[color:color-mix(in_srgb,var(--color-ok)_12%,transparent)]',
  },
  danger: {
    text: 'text-[color:var(--color-danger)]',
    surface: 'bg-[color:color-mix(in_srgb,var(--color-danger)_12%,transparent)]',
  },
  neutral: {
    text: 'text-[color:var(--color-text-muted)]',
    surface: 'bg-[color:var(--color-surface-sunken)]',
  },
  wait: {
    text: 'text-[color:var(--color-attention)]',
    surface: 'bg-[color:color-mix(in_srgb,var(--color-attention)_12%,transparent)]',
  },
} as const
const classes = computed(() => TONE[props.tone])
</script>

<template>
  <div
    class="flex items-center"
    :class="[
      classes.surface,
      size === 'sm'
        ? 'gap-[var(--space-tight)] rounded-full py-px pl-[var(--space-tight)] pr-[var(--space-inline)]'
        : 'gap-[var(--space-inline)] rounded-[var(--radius-control)] px-[var(--space-cell)] py-[var(--space-inline)]',
    ]"
  >
    <svg
      :width="size === 'sm' ? 14 : 16"
      :height="size === 'sm' ? 14 : 16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      class="shrink-0"
      :class="classes.text"
    >
      <path
        v-if="verdict && tone === 'ok'"
        d="M5.4 13.4H2.6V7.2h2.8zM5.4 7.2 8 2.4c1.2 0 1.8.8 1.6 2l-.4 2.2h3.2c.8 0 1.4.8 1.2 1.6l-1 4.2c-.2.6-.6 1-1.2 1H5.4"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        v-else-if="verdict && tone === 'danger'"
        d="M5.4 2.6H2.6v6.2h2.8zM5.4 8.8 8 13.6c1.2 0 1.8-.8 1.6-2l-.4-2.2h3.2c.8 0 1.4-.8 1.2-1.6l-1-4.2c-.2-.6-.6-1-1.2-1H5.4"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <path
        v-else-if="tone === 'wait'"
        d="M4 2.4h8M4 13.6h8M5 2.4c0 3.6 6 3.6 6 5.6s-6 2-6 5.6M11 2.4c0 3.6-6 3.6-6 5.6s6 2 6 5.6"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        v-else-if="tone === 'ok'"
        d="M3 8.4 6.4 11.8 13 5.2"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <circle
        v-else-if="tone === 'neutral'"
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <template v-else>
        <path
          d="M8 2.2 15 14H1z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
        />
        <path d="M8 6.4v3.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
        <circle cx="8" cy="11.8" r=".9" fill="currentColor" />
      </template>
      <path
        v-if="tone === 'neutral'"
        d="M5.5 8h5"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
      />
    </svg>
    <p
      class="font-medium"
      :class="[
        classes.text,
        size === 'sm'
          ? 'whitespace-nowrap text-[length:var(--text-step-1)]'
          : 'text-[length:var(--text-step-3)]',
      ]"
    >
      <slot />
    </p>
  </div>
</template>
